import type {
  SearchSavedSearchElasticSchemaWithIdNullable,
  SearchSavedSearchesSchemaWithId,
  SearchSavedSearchGroupSchema,
  SearchSavedSearchGroupSchemaWithId,
  SearchSavedSearchGroupSchemaWithIdNullable,
  SearchSavedSearchSchemaWithId,
  SearchSearchCriteriaSaved,
} from '@iconik-io/iconik-api/src/services/search';

import type { ObjectType } from '../types/object_types';

/**
 * The docs type criteria as a string, but the endpoint returns the criteria
 * object. https://linear.app/iconik/issue/BE-2766
 */
export type SearchSavedSearchElasticSchemaNullablePatched = Omit<
  SearchSavedSearchElasticSchemaWithIdNullable,
  'criteria'
> & {
  criteria?: SearchSearchCriteriaSaved | null;
};

export type SavedSearchItem = SearchSavedSearchElasticSchemaNullablePatched & {
  object_type: ObjectType;
};

export type SearchSavedSearchesSchemaPatched = Omit<
  SearchSavedSearchesSchemaWithId,
  'objects'
> & {
  objects: SearchSavedSearchElasticSchemaNullablePatched[];
};

export type SavedSearchGroupItem =
  SearchSavedSearchGroupSchemaWithIdNullable & {
    object_type: ObjectType;
  };

/**
 * Search answers with the nullable schemas while the store holds the
 * non-nullable ones, and nothing normalizes in between. The widenings are
 * stated here rather than at each reducer.
 */
export const asSavedSearchGroups = (
  groups: SearchSavedSearchGroupSchemaWithIdNullable[],
): SearchSavedSearchGroupSchemaWithId[] =>
  // biome-ignore lint/plugin: see above.
  groups as SearchSavedSearchGroupSchemaWithId[];

/**
 * A saved search reaches the store with its criteria object, which the
 * generated schema still declares as a string.
 */
export const asSavedSearch = (
  search: SearchSavedSearchSchemaWithId,
): SearchSavedSearchElasticSchemaNullablePatched => search;

/** A write response types its own id as optional, though one always comes. */
export const asSavedSearchGroup = (
  group:
    | SearchSavedSearchGroupSchemaWithIdNullable
    | SearchSavedSearchGroupSchema,
): SearchSavedSearchGroupSchemaWithId =>
  // biome-ignore lint/plugin: see asSavedSearchGroups.
  group as SearchSavedSearchGroupSchemaWithId;

/**
 * The generated response still declares the string criteria, so the patched
 * shape is stated once here rather than at each reducer.
 */
export const asSavedSearchesResponse = (
  body: SearchSavedSearchesSchemaWithId,
): SearchSavedSearchesSchemaPatched =>
  // biome-ignore lint/plugin: see above.
  body as SearchSavedSearchesSchemaPatched;
